import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import serverConfig from '../config/serverConfig';

// pdfMake.vfs = pdfFonts.pdfMake.vfs;

const ContractSignature = () => {
  const [step, setStep] = useState(0);
  const [contract, setContract] = useState(null);
  const [cgv, setCgv] = useState([]);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState('');
  const [formData, setFormData] = useState({
    signer_name: '',
    signer_email: '',
    cgv_accepted: false,
    ip_address: ''
  });
  const { id } = useParams();

  useEffect(() => {
    if (!id) {
      setError('No contract ID provided');
      setIsLoading(false);
      return;
    }
    fetchContract();
    fetchCgv();
    fetchIpAddress();
  }, [id]);

  const fetchIpAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      setFormData(prevData => ({
        ...prevData,
        ip_address: data.ip
      }));
    } catch (error) {
      console.error('Error fetching IP address:', error);
      // Fallback to empty string if IP fetch fails
      setFormData(prevData => ({
        ...prevData,
        ip_address: ''
      }));
    }
  };

  const fetchContract = async () => {
    try {
      const dataSend = { 'code': id };
      const response = await fetch(`${serverConfig.serverUrl}?action=contract&token=${serverConfig.authToken}`, {
        method: 'POST',
        body: JSON.stringify(dataSend),
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      // console.log(data);
      setContract(data);
      setIsLoading(false);

    } catch (e) {
      console.error('Error fetching contract: ', e);
    }
  };

  const fetchCgv = async () => {
    try {
      const response = await fetch(`${serverConfig.serverUrl}?action=cgv&token=${serverConfig.authToken}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      setCgv(data);
    } catch (error) {
      console.error('Error sending request', error);
    }
  };

  const processTableCell = (cell) => {
    const content = [];

    // Process text content with precise line break control
    const textNodes = Array.from(cell.childNodes)
      .map(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent.trim();
        } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'br') {
          return '\n';
        }
        return '';
      })
      .filter(text => text !== ''); // Remove empty strings

    // Join text nodes with proper spacing
    if (textNodes.length > 0) {
      content.push({
        text: textNodes.join(''),
        margin: [0, 0, 0, 2]  // Small bottom margin for text
      });
    }

    // Add image if present
    const img = cell.querySelector('img');
    if (img) {
      const imgSrc = img.getAttribute('src');
      if (imgSrc && imgSrc.startsWith('data:image')) {
        content.push({
          image: imgSrc,
          width: parseInt(img.getAttribute('width')) || 120,
          alignment: 'left',
          margin: [0, 2, 0, 0]  // Small top margin for image
        });
      }
    }

    return {
      stack: content,
      margin: [2, 2, 2, 2]  // Cell padding
    };
  };

  const htmlToPdfmakeContent = (htmlString) => {
    const container = document.createElement('div');
    container.innerHTML = htmlString;

    const processInlineElements = (element) => {
      const content = [];
      for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.trim()) {
            content.push(node.textContent.trim());
          }
        } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'br') {
          content.push('\n');
        }
      }
      return content.join('');
    };

    const convertElement = (element) => {
      const result = [];

      for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          // Handle simple text
          if (node.textContent.trim()) {
            result.push({ text: node.textContent.trim() });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Handle HTML elements
          let content = convertElement(node);

          switch (node.tagName.toLowerCase()) {
            case 'br':
              // Handle line breaks by adding a new line
              result.push({ text: '\n' });
              break;

            case 'img':
              // Handle images
              const imgSrc = node.getAttribute('src');
              if (imgSrc && imgSrc.startsWith('data:image')) {
                try {
                  // Extract base64 data
                  const base64Data = imgSrc.split(',')[1];

                  // Convert base64 to binary
                  const binaryString = window.atob(base64Data);

                  // Convert binary string to Uint8Array
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }

                  result.push({
                    image: bytes,
                    width: parseInt(node.getAttribute('width')) || 200,
                    height: parseInt(node.getAttribute('height')),
                    margin: [0, 5, 0, 5]
                  });
                } catch (error) {
                  console.error('Error processing image:', error);
                  result.push({
                    text: '[Image processing error]',
                    color: 'red',
                    fontSize: 10,
                    margin: [0, 5, 0, 5]
                  });
                }
              }
              break;

            case 'h1':
              result.push({ text: node.textContent, fontSize: 24, bold: true, margin: [0, 10, 0, 5] });
              break;

            case 'h2':
              result.push({ text: node.textContent, fontSize: 20, bold: true, margin: [0, 8, 0, 4] });
              break;

            case 'h3':
              result.push({ text: node.textContent, fontSize: 16, bold: true, margin: [0, 6, 0, 3] });
              break;

            case 'p':
              // Handle paragraphs with potential line breaks
              const paragraphContent = [];
              for (const childNode of node.childNodes) {
                if (childNode.nodeType === Node.TEXT_NODE) {
                  if (childNode.textContent.trim()) {
                    paragraphContent.push({ text: childNode.textContent.trim() });
                  }
                } else if (childNode.tagName && childNode.tagName.toLowerCase() === 'br') {
                  paragraphContent.push({ text: '\n' });
                }
              }
              result.push({
                text: paragraphContent,
                margin: [0, 5, 0, 5]
              });
              break;

            case 'ul':
              result.push({
                ul: Array.from(node.children).map(li => ({
                  text: processInlineElements(li),
                  margin: [0, 2, 0, 2]
                }))
              });
              break;

            case 'ol':
              result.push({
                ol: Array.from(node.children).map(li => ({
                  text: processInlineElements(li),
                  margin: [0, 2, 0, 2]
                }))
              });
              break;

            case 'table':
              const tableBody = [];
              for (const row of node.rows) {
                const tableRow = [];
                for (const cell of row.cells) {
                  tableRow.push(processTableCell(cell));
                }
                tableBody.push(tableRow);
              }

              result.push({
                table: {
                  headerRows: 1,
                  widths: Array(node.rows[0]?.cells.length || 0).fill('*'),
                  body: tableBody
                },
                margin: [0, 5, 0, 5]
              });
              break;

            default:
              if (content.length > 0) {
                result.push(...content);
              }
          }
        }
      }

      return result;
    };

    return convertElement(container);
  };

  // Function to convert URL image to base64
  const getBase64ImageFromURL = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading image:', error);
      throw error;
    }
  };

  const generatePDF = async () => {
    try {
      // Get the logo first
      const logoResponse = await fetch('https://i.ibb.co/0C7DXLy/Dark.png');
      const logoBlob = await logoResponse.blob();
      const logoBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(logoBlob);
      });

      // Create the document definition
      const docDefinition = {
        header: {
          image: logoBase64,
          width: 80,
          margin: [0, 20, 40, 0],
          alignment: 'right'
        },
        content: [
          ...htmlToPdfmakeContent(contract.content),
          { text: '\n\n' },
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 }] },
          { text: '\n' },
          {
            text: `Signé électroniquement par ${formData.signer_name} (${formData.signer_email})`,
            color: '#0459e0'
          },
          {
            text: `Le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
            color: '#0459e0'
          },
          {
            text: `IP: ${formData.ip_address}`,
            color: '#0459e0'
          }
        ],
        defaultStyle: {
          font: 'Roboto',
          fontSize: 11
        },
        pageMargins: [50, 60, 50, 50]
      };

      return new Promise((resolve, reject) => {
        const pdfDoc = pdfMake.createPdf(docDefinition);

        pdfDoc.getBlob(async (blob) => {
          try {
            // Upload the PDF
            const formData = new FormData();
            formData.append('pdf', blob, `contrat_${contract.signing_link}.pdf`);

            const response = await fetch(
              `${serverConfig.serverUrl}?action=upload&token=${serverConfig.authToken}`,
              {
                method: 'POST',
                body: formData,
              }
            );

            if (!response.ok) {
              throw new Error(await response.text());
            }

            const result = await response.json();
            // console.log(result);

            resolve(result);
            return result;
            // If the server returns the URL of the uploaded PDF
            if (result.path) {
              window.open(result.path, '_blank');
            }
          } catch (error) {
            console.error('Error handling PDF:', error);
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  };

  const blobPdf = async (docDefinition) => {
    try {
      return new Promise((resolve, reject) => {
        const pdfDoc = pdfMake.createPdf(docDefinition);

        pdfDoc.getBlob(async (blob) => {
          try {
            const formData = new FormData();
            formData.append('pdf', blob, `contrat_${contract.signing_link}.pdf`);

            const response = await fetch(
              `${serverConfig.serverUrl}?action=upload&token=${serverConfig.authToken}`,
              {
                method: 'POST',
                body: formData,
              }
            );

            if (response.ok) {
              const result = await response.json();
              // console.log('File saved successfully:', result.path);
              // setStep(3);
              resolve(result);
            } else {
              const error = await response.text();
              console.error('Failed to save file:', error);
              reject(new Error(error));
            }
          } catch (error) {
            console.error('Error in blob handling:', error);
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('Error in blobPdf:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    const response = await fetch(`${serverConfig.serverUrl}?action=signature&token=${serverConfig.authToken}`, {
      method: 'POST',
      body: JSON.stringify({
        contract_id: contract.id,
        ...formData
      }),
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();
    // console.log(data);
    if (data === true) {
      const result = await generatePDF();
      if (result?.path) {
        setPdfUrl(result.path);
      }
      setStep(4); // Étape de confirmation
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `contrat.pdf`; // Forces download
    link.click();
  };

  const verifyPassword = () => {
    if (password === contract.password) {
      setError('');
      setStep(1);
    } else {
      setError('Mot de passe incorrect');
    }
  };

  if (isLoading) {
    return (
      <div className="container min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading contract...</p>
          </div>
        </div>
      </div>
    );
  }

  if (contract?.status == 'signed') {
    return (
      <div className="container vh-100">
        <div className="row justify-content-center align-items-center h-100">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body">
                <div className="text-center">
                  <h3 className="text-success">Ce contrat est déjà signé</h3>
                  <p>Une copie du contrat au format PDF vous a déjà été envoyée par email.</p>
                  <p>Si vous ne l'avez pas, merci de contacter Karib Tech.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (contract?.status == 'expired' || contract?.status == 'draft') {
    return (
      <div className="container vh-100">
        <div className="row justify-content-center align-items-center h-100">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body">
                <div className="text-center">
                  <h3 className="text-danger">Une erreur est survenue</h3>
                  <p>Une erreur est survenue, merci de réessayer.</p>
                  <p>Si cela persiste, merci de contacter Krib Tech.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container vh-100">
      <div className="row justify-content-center align-items-center h-100">
        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              {step === 0 ? (
                <div>
                  <h3>Accès au contrat</h3>
                  <div className="mb-3">
                    <label className="form-label">Veuillez entrer le mot de passe</label>
                    <input
                      type="password"
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mot de passe"
                    />
                    {error && <div className="text-danger mt-2">{error}</div>}
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={verifyPassword}
                    disabled={!password}
                  >
                    Accéder au contrat
                  </button>
                </div>
              ) : (
                <>
                  <style>
                    {`
                                  .contract-content table {
                                      width: 100%;
                                      margin-bottom: 1rem;
                                      border-collapse: collapse;
                                  }
                                  .contract-content table td,
                                  .contract-content table th {
                                      border: 1px solid #dee2e6;
                                      padding: 8px;
                                  }
                                  `}
                  </style>
                  <div className="progress mb-4">
                    <div className="progress-bar" style={{ width: `${step * 25}%` }}></div>
                  </div>
                </>
              )}

              {step === 1 && (
                <div>
                  <h3>Contrat</h3>
                  <div className="border p-3 mb-3" style={{ height: '300px', overflowY: 'auto' }}>
                    {/* {contract?.content} */}
                    <div className="contract-content" dangerouslySetInnerHTML={{ __html: contract?.content }} />
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => setStep(2)}
                  >
                    Suivant
                  </button>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h3>CGV</h3>
                  <div className="border p-3 mb-3" style={{ height: '300px', overflowY: 'auto' }}>
                    <div className="contract-content" dangerouslySetInnerHTML={{ __html: cgv?.content }} />
                  </div>
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      id="cgv-checkbox"
                      className="form-check-input"
                      checked={formData.cgv_accepted}
                      onChange={(e) => setFormData({ ...formData, cgv_accepted: e.target.checked })}
                    />
                    <label htmlFor="cgv-checkbox" className="form-check-label">
                      J'accepte les CGV
                    </label>
                  </div>
                  <button
                    className="btn btn-primary"
                    disabled={!formData.cgv_accepted}
                    onClick={() => setStep(3)}
                  >
                    Suivant
                  </button>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h3>Signature</h3>
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Nom complet"
                      value={formData.signer_name}
                      onChange={(e) => setFormData({ ...formData, signer_name: e.target.value })}
                    />
                    <input
                      type="email"
                      className="form-control mb-3"
                      placeholder="Email"
                      value={formData.signer_email}
                      onChange={(e) => setFormData({ ...formData, signer_email: e.target.value })}
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={!formData.signer_name || !formData.signer_email}
                  >
                    Signer le contrat
                  </button>
                </div>
              )}

              {step === 4 && (
                <div className="text-center">
                  <h3 className="text-success">Contrat signé !</h3>
                  <p>Une copie du contrat au format PDF a été générée.</p>
                  <p>Une copie vous sera également envoyée par email.</p>
                  <button
                    onClick={handleDownload}
                    className="btn btn-primary mt-3"
                  >
                    Télécharger le contrat PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractSignature;